import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CurrencyCircleDollar,
  Percent,
  CheckCircle,
  Sparkle,
  ArrowRight,
  Wallet,
  Info,
  ShieldCheck,
  WarningCircle,
} from '@phosphor-icons/react';
import {
  apiClient,
  formatCurrency,
  useUpgradeStore,
  useAuthStore,
  PLAN_TRANSACTION_FEES,
  PLAN_PRICING,
} from '@dukene/shared';

interface SubscriptionOverview {
  currentPlan: 'BASIC' | 'PRO' | 'MAX' | 'ENTERPRISE';
  monthlyCost: number;
  transactionFeePercentage?: number;
  storeLimit?: number | null;
  salesChannelLimit?: number | null;
  walletBalance?: number;
}

const TIERS = [
  {
    id: 'BASIC',
    name: 'Basic',
    monthlyPrice: PLAN_PRICING.BASIC,
    transactionFee: PLAN_TRANSACTION_FEES.BASIC,
    storeLimit: '1 Store',
    channelLimit: '1 Sales Channel (Online Store)',
    badge: 'FREE FOREVER',
    description: 'Perfect for new merchants starting their online commerce journey.',
    features: [
      '6.0% platform fee per transaction',
      '1 active storefront',
      'Online store sales channel',
      'Credits wallet auto-deductions',
      'Standard email support',
    ],
  },
  {
    id: 'PRO',
    name: 'Pro',
    monthlyPrice: PLAN_PRICING.PRO,
    transactionFee: PLAN_TRANSACTION_FEES.PRO,
    storeLimit: 'Up to 2 Stores',
    channelLimit: '3 Sales Channels per store',
    badge: 'MOST POPULAR',
    description: 'For growing businesses expanding across social and physical channels.',
    features: [
      '3.0% platform fee (50% lower fee rate)',
      'Up to 2 stores included',
      '3 sales channels per store (Meta, Google, POS, WhatsApp)',
      'Priority transaction routing',
      'Fast-track support',
    ],
  },
  {
    id: 'MAX',
    name: 'Max',
    monthlyPrice: PLAN_PRICING.MAX,
    transactionFee: PLAN_TRANSACTION_FEES.MAX,
    storeLimit: 'Up to 5 Stores',
    channelLimit: 'All Sales Channels (Unlimited)',
    badge: 'BEST VALUE FOR VOLUME',
    description: 'For multi-store retailers and high-volume merchant operations.',
    features: [
      '2.0% platform fee (Lowest public rate)',
      'Up to 5 stores included',
      'All sales channels without restrictions',
      'Advanced multi-store analytics',
      'Dedicated account manager',
    ],
  },
  {
    id: 'ENTERPRISE',
    name: 'Enterprise',
    monthlyPrice: null,
    transactionFee: PLAN_TRANSACTION_FEES.ENTERPRISE,
    storeLimit: 'Unlimited Stores',
    channelLimit: 'Unlimited Channels',
    badge: 'CUSTOM VOLUME',
    description: 'For enterprises demanding zero platform transaction fees and custom infrastructure.',
    features: [
      '0.0% platform transaction fee',
      'Unlimited stores & custom domains',
      'Custom ERP & payment gateway integrations',
      '24/7 Phone support & 99.9% uptime SLA',
      'Custom contractual billing',
    ],
  },
];

export default function PricingSettings() {
  const { user } = useAuthStore();
  const [calcAmount, setCalcAmount] = useState<string>('2500');

  const isOwner =
    user?.role === 'OWNER' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'MERCHANT' ||
    user?.membershipRole === 'OWNER' ||
    user?.membershipRole === 'CO_OWNER';

  const { data: overview, isLoading } = useQuery<SubscriptionOverview>({
    queryKey: ['billing-overview'],
    queryFn: () => apiClient.get('/billing/overview'),
    enabled: isOwner,
  });

  if (!isOwner) {
    return (
      <div className="py-16 text-center max-w-md mx-auto space-y-3">
        <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-950 flex items-center justify-center text-amber-700 dark:text-amber-300 mx-auto">
          <WarningCircle weight="fill" className="w-6 h-6" />
        </div>
        <h2 className="text-base font-bold text-gray-900 dark:text-white">Owner access only</h2>
      </div>
    );
  }

  const currentPlan = overview?.currentPlan || user?.plan || 'BASIC';
  const currentFeeRate = PLAN_TRANSACTION_FEES[currentPlan] ?? 6;
  const numericAmount = Math.max(0, Number(calcAmount) || 0);

  // Computed fees for calculator
  const feeBasic = (numericAmount * (PLAN_TRANSACTION_FEES.BASIC / 100));
  const feePro = (numericAmount * (PLAN_TRANSACTION_FEES.PRO / 100));
  const feeMax = (numericAmount * (PLAN_TRANSACTION_FEES.MAX / 100));

  return (
    <div className="space-y-8 pb-20 max-w-5xl animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#D2D5D9] dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center text-emerald-800 dark:text-emerald-300">
              <Percent weight="bold" className="w-4 h-4" />
            </div>
            <h1 className="text-xl font-bold text-[#202223] dark:text-white">Transaction Fee Pricing</h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/settings/wallet"
            className="px-3.5 py-1.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-semibold text-[#202223] dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors inline-flex items-center gap-1.5 shadow-2xs"
          >
            <Wallet weight="fill" className="w-3.5 h-3.5 text-emerald-600" />
            Credits Wallet
          </Link>
          <button
            type="button"
            onClick={() => useUpgradeStore.getState().open('transaction_fees')}
            className="px-4 py-1.5 rounded-lg bg-[#202223] dark:bg-white text-white dark:text-slate-900 text-xs font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-1.5 shadow-sm"
          >
            <Sparkle weight="fill" className="w-3.5 h-3.5" />
            Upgrade Tier
          </button>
        </div>
      </div>

      {/* Active Plan Banner Card */}
      <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-2xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] font-bold bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300">
              <CheckCircle weight="fill" className="w-3.5 h-3.5" />
              YOUR ACTIVE TIER
            </span>
            <span className="px-2.5 py-0.5 rounded text-[11px] font-bold bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300">
              {currentFeeRate}% TRANSACTION FEE
            </span>
          </div>

          <div>
            <h2 className="text-2xl font-black text-[#202223] dark:text-white">
              {isLoading ? 'Loading plan...' : `${currentPlan} Plan`}
            </h2>
          </div>
        </div>

        <div className="bg-[#F8F9FA] dark:bg-slate-800/60 p-4 rounded-xl border border-[#E1E4E8] dark:border-slate-700 min-w-[220px] space-y-2">
          <div className="flex items-center justify-between text-xs text-[#5C5F62] dark:text-slate-400 font-medium">
            <span>Available Credits:</span>
            <strong className="text-[#202223] dark:text-white">
              {overview?.walletBalance !== undefined ? formatCurrency(overview.walletBalance) : 'KES 200.00'}
            </strong>
          </div>
          <Link
            to="/settings/wallet"
            className="w-full py-2 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1.5 shadow-2xs"
          >
            <Wallet weight="fill" className="w-3.5 h-3.5" />
            Top Up Credits
          </Link>
        </div>
      </div>

      {/* Interactive Fee & Savings Calculator */}
      <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-[#E1E4E8] dark:border-slate-800">
          <div className="flex items-center gap-2">
            <CurrencyCircleDollar weight="fill" className="w-5 h-5 text-emerald-600" />
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">
              Transaction Fee & Savings Calculator
            </h3>
          </div>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400">
            See how much you save on platform fees by upgrading
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-3">
            <label className="block text-xs font-bold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
              Enter Order / Transaction Amount (KES)
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                KES
              </span>
              <input
                type="number"
                min="10"
                step="50"
                value={calcAmount}
                onChange={(e) => setCalcAmount(e.target.value)}
                placeholder="2500"
                className="w-full h-11 pl-12 pr-4 rounded-xl border border-gray-300 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-base font-bold text-gray-900 dark:text-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white"
              />
            </div>
            <p className="text-[11px] text-[#5C5F62] dark:text-slate-400 flex items-center gap-1">
              <Info weight="fill" className="w-3.5 h-3.5 text-gray-400" />
              Calculations reflect platform fee deductions.
            </p>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Basic Card */}
            <div className={`p-4 rounded-xl border flex flex-col justify-between ${
              currentPlan === 'BASIC'
                ? 'bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-500'
                : 'bg-[#F8F9FA] dark:bg-slate-800/40 border-[#E1E4E8] dark:border-slate-700'
            }`}>
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#202223] dark:text-white">Basic (6%)</span>
                  {currentPlan === 'BASIC' && (
                    <span className="text-[9px] font-bold uppercase bg-emerald-600 text-white px-1.5 py-0.2 rounded">
                      Current
                    </span>
                  )}
                </div>
                <p className="text-lg font-black text-[#202223] dark:text-white mt-2">
                  {formatCurrency(feeBasic)}
                </p>
                <p className="text-[10px] text-[#5C5F62] dark:text-slate-400 mt-0.5">Free Subscription</p>
              </div>
              <span className="text-[11px] text-gray-400 font-medium mt-3 block">Baseline rate</span>
            </div>

            {/* Pro Card */}
            <div className={`p-4 rounded-xl border flex flex-col justify-between ${
              currentPlan === 'PRO'
                ? 'bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-500'
                : 'bg-[#F8F9FA] dark:bg-slate-800/40 border-[#E1E4E8] dark:border-slate-700'
            }`}>
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#202223] dark:text-white">Pro (3%)</span>
                  {currentPlan === 'PRO' && (
                    <span className="text-[9px] font-bold uppercase bg-emerald-600 text-white px-1.5 py-0.2 rounded">
                      Current
                    </span>
                  )}
                </div>
                <p className="text-lg font-black text-emerald-600 dark:text-emerald-400 mt-2">
                  {formatCurrency(feePro)}
                </p>
                <p className="text-[10px] text-[#5C5F62] dark:text-slate-400 mt-0.5">KES 3,500 / month</p>
              </div>
              <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-bold mt-3 block">
                Save {formatCurrency(Math.max(0, feeBasic - feePro))} per order
              </span>
            </div>

            {/* Max Card */}
            <div className={`p-4 rounded-xl border flex flex-col justify-between ${
              currentPlan === 'MAX'
                ? 'bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-500'
                : 'bg-[#F8F9FA] dark:bg-slate-800/40 border-[#E1E4E8] dark:border-slate-700'
            }`}>
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#202223] dark:text-white">Max (2%)</span>
                  {currentPlan === 'MAX' && (
                    <span className="text-[9px] font-bold uppercase bg-emerald-600 text-white px-1.5 py-0.2 rounded">
                      Current
                    </span>
                  )}
                </div>
                <p className="text-lg font-black text-emerald-600 dark:text-emerald-400 mt-2">
                  {formatCurrency(feeMax)}
                </p>
                <p className="text-[10px] text-[#5C5F62] dark:text-slate-400 mt-0.5">KES 9,500 / month</p>
              </div>
              <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-bold mt-3 block">
                Save {formatCurrency(Math.max(0, feeBasic - feeMax))} per order
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tier Pricing Breakdown Grid */}
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-bold text-[#202223] dark:text-white">
            Platform Tier Fee Comparison
          </h2>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
            Compare transaction fee percentages and capabilities across all plans.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((tier) => {
            const isCurrent = currentPlan === tier.id;
            return (
              <div
                key={tier.id}
                className={`rounded-2xl p-5 border transition-all flex flex-col justify-between ${
                  isCurrent
                    ? 'bg-white dark:bg-slate-900 border-2 border-emerald-500 shadow-md ring-4 ring-emerald-500/10'
                    : 'bg-white dark:bg-slate-900 border-[#D2D5D9] dark:border-slate-800 shadow-sm hover:border-gray-400'
                }`}
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      isCurrent
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300'
                    }`}>
                      {tier.badge}
                    </span>
                    {isCurrent && (
                      <span className="text-[11px] font-bold text-emerald-600 flex items-center gap-1">
                        <CheckCircle weight="fill" className="w-3.5 h-3.5" />
                        Current
                      </span>
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-[#202223] dark:text-white">{tier.name}</h3>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
                        {tier.transactionFee}%
                      </span>
                      <span className="text-xs font-semibold text-[#5C5F62] dark:text-slate-400">
                        Tx Fee
                      </span>
                    </div>
                    <p className="text-xs font-semibold text-[#202223] dark:text-white mt-1">
                      {tier.monthlyPrice === null
                        ? 'Custom Enterprise Pricing'
                        : tier.monthlyPrice === 0
                        ? 'Free monthly subscription'
                        : `${formatCurrency(tier.monthlyPrice)} / month`}
                    </p>
                  </div>

                  <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                    {tier.description}
                  </p>

                  <div className="pt-3 border-t border-[#E1E4E8] dark:border-slate-800 space-y-2">
                    {tier.features.map((feat, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs text-[#202223] dark:text-slate-200">
                        <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-6">
                  {isCurrent ? (
                    <button
                      type="button"
                      disabled
                      className="w-full py-2 px-3 rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-500 text-xs font-bold cursor-default"
                    >
                      Active Plan
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => useUpgradeStore.getState().open('transaction_fees')}
                      className="w-full py-2 px-3 rounded-xl bg-black dark:bg-white text-white dark:text-slate-900 text-xs font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-1.5 shadow-sm"
                    >
                      <span>Switch to {tier.name}</span>
                      <ArrowRight weight="bold" className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fee Policy FAQ Note */}
      <div className="bg-[#F8F9FA] dark:bg-slate-900/60 border border-[#D2D5D9] dark:border-slate-800 rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
          <ShieldCheck weight="fill" className="w-4 h-4 text-emerald-600" />
          <span>How transaction fee deductions work</span>
        </div>
        <p className="text-xs text-[#5C5F62] dark:text-slate-300 leading-relaxed">
          Because customers make payments directly to your own Till, Paybill, or bank account, Dukene does not hold your sales funds. Instead, platform fees (6% on Basic, 3% on Pro, 2% on Max) are debited from your <Link to="/settings/wallet" className="text-emerald font-semibold underline">Credits Wallet</Link> in real time. Maintain a positive credits balance to ensure uninterrupted checkout processing.
        </p>
      </div>
    </div>
  );
}
